/**
 * How much of the COP stage is covered by chrome floating over it.
 *
 * The tactical shell makes the map full bleed and floats the bars, panels and
 * chips on top of it. That is the whole point - a bar appearing no longer resizes
 * the MapLibre canvas, so the aircraft the operator is tracking stays where it is
 * (`DroneMap.tsx:781`). But it introduces a second problem in place of the first:
 * the canvas is now BIGGER than the map anyone can see, and three decisions that
 * used to read the canvas are really asking about the visible part.
 *
 * - Whether there is room to fly manually at all, and the refusal that states the
 *   measurement ("Control surface is 210 px, manual flight needs 240").
 * - Where the grab halo is drawn, which has to land ON the aircraft symbol.
 * - Where an anchored card may sit without falling behind a panel.
 *
 * Answer any of those from the canvas and the COP offers manual flight into a
 * surface half of which is under a panel, or draws the halo underneath the
 * command bar. That is a flight hazard, not a cosmetic one, which is why this
 * arithmetic is a pure module with its own tests rather than a few subtractions
 * inline in `page.tsx`.
 *
 * Pixels are CSS px throughout, and every inset is a distance INWARD from that
 * edge of the stage. All zero means nothing floats, which is the classic shell
 * and also every stage that has not measured yet.
 */
export type StageEdge = 'top' | 'right' | 'bottom' | 'left';

export interface StageInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export const NO_STAGE_INSETS: StageInsets = Object.freeze({
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
});

/** One floating host's claim on one edge. `sizePx` is its height or its width. */
export interface StageInsetContribution {
  edge: StageEdge;
  sizePx: number;
}

function sane(px: number): number {
  return Number.isFinite(px) && px > 0 ? px : 0;
}

/**
 * Fold every host's claim into one record, taking the LARGEST claim per edge.
 *
 * Largest rather than sum, because hosts on the same edge overlap rather than
 * stack: the bottom stack and a launch card both sit at the bottom of the stage,
 * one inside the other. Summing them would report the map as smaller than it is
 * and refuse manual flight that is perfectly available.
 */
export function combineStageInsets(
  contributions: Iterable<StageInsetContribution>,
): StageInsets {
  const insets: StageInsets = { ...NO_STAGE_INSETS };
  for (const { edge, sizePx } of contributions) {
    insets[edge] = Math.max(insets[edge], sane(sizePx));
  }
  return insets;
}

/**
 * The insets that apply to a box hosted INSIDE the stage.
 *
 * The 280 px control minimap of the VIDEO layout is the exception the whole
 * function exists for: `ManualStage` lifts it above the bottom stack, so nothing
 * floats over it and subtracting the stack's height from it would report a
 * control surface 60-odd px smaller than the one the operator is looking at.
 *
 * That mattered more than it sounds. The MANUAL verdict is computed from this
 * measurement, and a false refusal there does not just grey out a key: an RC
 * override lands in `UNAVAILABLE` rather than `OVERRIDE`, the picture the
 * operator was watching is torn down, and the MANUAL key refuses with a
 * measurement of a box that is not the control surface.
 *
 * One rule, one place, because it has to hold identically for the box that is
 * measured and the surface that is drawn.
 */
export function stageInsetsForHost(isLiftedClear: boolean, insets: StageInsets): StageInsets {
  return isLiftedClear ? NO_STAGE_INSETS : insets;
}

/**
 * Drop the insets for stage edges a box does not actually REACH.
 *
 * `stageInsetsForHost` answers "is anything lifted clear of the chrome". This
 * answers the other half: a box that occupies only part of the stage cannot be
 * covered by chrome anchored to an edge it does not touch.
 *
 * The SPLIT layout is the case this exists for. The map is the left half and the
 * picture is the right half, so the stage's right edge belongs to the PICTURE.
 * The side panel is at most half the viewport (`clampDockSheetWidth`), so in
 * SPLIT it covers none of the map at all - and subtracting its width from the
 * map half reported a control surface up to 560 px smaller than the one the
 * operator is looking at. On an 1194 px tablet that is a MANUAL key refusing
 * with "Control surface is 271 px" about a 591 px box, and mid-session it is
 * `ring.controllable` flipping false and handing the camera lock back while the
 * aircraft is still airborne.
 *
 * An edge left out of `reaches` is assumed reached, so the safe direction is the
 * default: a box that is covered and says nothing still gets its inset.
 */
export function stageInsetsForReachedEdges(
  insets: StageInsets,
  reaches: Partial<Record<StageEdge, boolean>>,
): StageInsets {
  const next: StageInsets = { ...insets };
  for (const edge of ['top', 'right', 'bottom', 'left'] as const) {
    if (reaches[edge] === false) next[edge] = 0;
  }
  return next;
}

/**
 * The height cap for a panel anchored to the TOP of the stage that must not run
 * under the chrome floating at the bottom of it.
 *
 * The left rail (alarm HUD above the strip board) and the zones panel are both
 * `top-2` with a cap, so the cap is the only thing deciding where their bottom
 * lands. Under the tactical shell the map runs to the bottom of the column and
 * the bottom stack floats at `z-30` over their `z-10`, so an uncapped panel puts
 * its last rows behind the command bar - opaque, and unclickable, because each
 * bar's wrapper is `pointer-events-auto`. The strip board's rows are real
 * buttons carrying `aria-pressed`, so that hides aircraft from the roster AND
 * fails WCAG 2.4.11 for anyone reaching them by keyboard.
 *
 * `edgeGapPx` is the gap the panel keeps at the top and bottom of the stage, in
 * CSS px - 8 for a `top-2` panel, which is where the existing `calc(100% - 16px)`
 * comes from. One function rather than the subtraction written at each call site,
 * so the two panels cannot drift apart the way the rail and the zones panel did.
 *
 * `topInsetPx` since #505 slice 7, when `CopTopStack` made the top edge a real
 * publisher for the first time. A top-anchored panel that is PUSHED DOWN by an
 * inset has that much less room before it reaches the bottom, so the height has
 * to lose it too - otherwise moving the strip board clear of the dock roster is
 * what pushes its last rows behind the command bar.
 */
export function stageTopAnchoredMaxHeight(
  bottomInsetPx: number,
  topInsetPx = 0,
  edgeGapPx = 8,
): string {
  return `calc(100% - ${edgeGapPx * 2}px - ${sane(bottomInsetPx)}px - ${sane(topInsetPx)}px)`;
}

/**
 * How far the centre of the visible map sits from the centre of the canvas.
 *
 * MapLibre's `easeTo({ offset })` is measured from the CONTAINER centre, and the
 * container is the whole canvas. A control surface drawn inside the uncovered
 * rectangle computes its anchor offset from ITS centre, so the two centres have
 * to be reconciled or the halo lands off the symbol by exactly this much.
 *
 * Screen pixels grow down and right, matching `manual-vector.ts` convention 1: a
 * bottom inset moves the visible centre UP, which is a negative `dyPx`.
 */
export function stageCentreShiftPx(insets: StageInsets): { dxPx: number; dyPx: number } {
  return {
    dxPx: (sane(insets.left) - sane(insets.right)) / 2,
    dyPx: (sane(insets.top) - sane(insets.bottom)) / 2,
  };
}
